import { useState, useContext, useEffect, useCallback, useMemo } from "react";
import debounce from "lodash.debounce";
import Commands from "../../model/Command";
import {
  Grid,
  Typography,
  Box,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import useAxios from "../../hooks/useAxios";
import { baseUrl } from "../../constants";
import SectorContext from "../../context/SectorContext";
import CommandList from "./CommandList";
import { CommandEntry, CommandListEntry } from "../../model/CommandEntry";
import { PaginatedResult } from "../../model/PaginatedResult";

const CommandGenerator = () => {
  const [command, setCommand] = useState("habitations");
  const [activity, setOnlyActive] = useState("active");
  const [rampMode, setRampMode] = useState(false);
  const [lotIdInput, setLotIdInput] = useState("");
  const [lotId, setLotId] = useState("");
  const debouncedSetLotId = useMemo(() => debounce((value: string) => setLotId(value), 500), []);
  const [commands, setCommands] = useState<Array<CommandListEntry>>([]);

  const [totalCount, setTotalCount] = useState(0);
  const [axiosInstance] = useAxios();
  const { sector } = useContext(SectorContext);

  const handleCommandChange = (event: any) => {
    setCommands([]);
    setCommand(event.target.value);
  };

  const handleSetActiveChange = (event: any) => {
    setOnlyActive(event.target.value);
  };

  const mapToCommandListEntry = useCallback(
    (commandEntry: CommandEntry): CommandListEntry => {
      return {
        id: commandEntry.id,
        command: `${rampMode ? "deck" : ""} ${command} ${commandEntry.target}`,
        lastModified: commandEntry.lastModified
          ? new Date(commandEntry.lastModified).toLocaleDateString()
          : "",
      };
    },
    [command, rampMode]
  );

  const getCommandResults = useCallback(
    async (skip: Number) => {
      var response = await axiosInstance.get<PaginatedResult<CommandEntry>>(
        `${baseUrl}/targets`,
        {
          params: {
            command,
            activity,
            take: 50,
            skip,
            idSector: sector,
            ...(command === "connect" && lotId ? { lotId } : {}),
          },
        }
      );
      setCommands((currentCommands) => [
        ...currentCommands,
        ...response.data.results.map(mapToCommandListEntry),
      ]);
      setTotalCount(response.data.totalCount);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [axiosInstance, command, activity, sector, rampMode, lotId]
  );

  const handleCheckBoxChange = (event: any) => {
    setRampMode(event.target.checked);
  };

  useEffect(() => {
    setCommands([]);
    getCommandResults(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command, activity, rampMode, lotId]);

  return (
    <Box>
      <Box id="header" sx={{ marginLeft: "3rem", marginTop: "2.5rem" }}>
        <Grid container spacing={1}>
          <Grid size={12}>
            <Typography variant="h5">Génération de commandes</Typography>
          </Grid>
          <Grid size={2}>
            <TextField
              value={command}
              variant="standard"
              select
              fullWidth
              onChange={handleCommandChange}
              label="Commande"
            >
              {Object.keys(Commands)
                .filter((v) => isNaN(Number(v)))
                .map((x) => {
                  return (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  );
                })}
            </TextField>
          </Grid>
          {command !== "connect" && (
            <Grid size={2}>
              <TextField
                value={activity}
                variant="standard"
                select
                fullWidth
                onChange={handleSetActiveChange}
                label="Activité"
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs Seulement</MenuItem>
                <MenuItem value="placeholder">Placeholders</MenuItem>
              </TextField>
            </Grid>
          )}
          {command === "connect" && (
            <Grid size={2}>
              <TextField
                value={lotIdInput}
                variant="standard"
                fullWidth
                onChange={(e) => {
                  setLotIdInput(e.target.value);
                  debouncedSetLotId(e.target.value);
                }}
                label="LotId (proximité 15x15)"
                placeholder="Ex: 101"
              />
            </Grid>
          )}
          <Grid size={6}>
            <FormGroup>
              <FormControlLabel
                sx={{ marginLeft: "0.5rem", marginTop: "0.65rem" }}
                control={
                  <Checkbox
                    checked={rampMode}
                    onChange={handleCheckBoxChange}
                  />
                }
                label="Mode Rampe"
              />
            </FormGroup>
          </Grid>
          <Grid size={12}></Grid>
        </Grid>
      </Box>
      <CommandList
        fetch={getCommandResults}
        commands={commands}
        totalCount={totalCount}
      />
    </Box>
  );
};

export default CommandGenerator;
